/**
 * Types for the review_code tool
 * Comprehensive code review system for SmartStack projects
 */

// ============================================================================
// Review Categories
// ============================================================================

export type ReviewCategory =
  | 'security'
  | 'architecture'
  | 'hardcoded-values'
  | 'tests'
  | 'ai-hallucinations'
  | 'performance'
  | 'dead-code'
  | 'i18n'
  | 'accessibility';

export type ReviewSeverity = 'blocking' | 'critical' | 'warning' | 'info';

// ============================================================================
// Finding Types
// ============================================================================

export interface CodeReviewFinding {
  /** Unique ID (ex: SEC-001, ARCH-002) */
  id: string;
  /** Category of the finding */
  category: ReviewCategory;
  /** Severity level */
  severity: ReviewSeverity;
  /** Short title */
  title: string;
  /** Detailed description */
  description: string;
  /** File path (relative to project root) */
  file: string;
  /** Line number */
  line?: number;
  /** Column number */
  column?: number;
  /** Code snippet (max 100 chars) */
  code?: string;
  /** Suggested fix */
  suggestion: string;
  /** Can be auto-fixed */
  autoFixable: boolean;
  /** CWE ID for security issues */
  cweId?: string;
  /** References to documentation */
  references?: string[];
}

// ============================================================================
// Result Types
// ============================================================================

export interface CodeReviewSummary {
  /** Overall status */
  status: 'passed' | 'failed' | 'warning';
  /** Score 0-100 */
  score: number;
  /** Letter grade */
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
  /** Summary message */
  message: string;
}

export interface CodeReviewStats {
  filesScanned: number;
  linesScanned: number;
  duration: number; // ms
  findingsCount: {
    blocking: number;
    critical: number;
    warning: number;
    info: number;
  };
  byCategory: Record<ReviewCategory, number>;
}

export interface CodeReviewResult {
  summary: CodeReviewSummary;
  findings: CodeReviewFinding[];
  stats: CodeReviewStats;
}

// ============================================================================
// Check Function Types
// ============================================================================

export interface CheckContext {
  projectPath: string;
  files: FileInfo[];
  scope: 'all' | 'changed' | 'staged';
}

export interface FileInfo {
  path: string;
  relativePath: string;
  content: string;
  language: 'csharp' | 'typescript' | 'javascript' | 'tsx' | 'jsx' | 'css' | 'json' | 'other';
  layer?: 'domain' | 'application' | 'infrastructure' | 'api' | 'web';
  lineCount: number;
}

export type CheckFunction = (context: CheckContext) => Promise<CodeReviewFinding[]>;

// ============================================================================
// Architecture Layer Rules
// ============================================================================

export const LAYER_RULES: Record<string, { canImport: string[]; cannotImport: string[] }> = {
  domain: {
    canImport: [],
    cannotImport: ['application', 'infrastructure', 'api', 'web'],
  },
  application: {
    canImport: ['domain'],
    cannotImport: ['infrastructure', 'api', 'web'],
  },
  infrastructure: {
    canImport: ['domain', 'application'],
    cannotImport: ['api', 'web'],
  },
  api: {
    canImport: ['domain', 'application', 'infrastructure'],
    cannotImport: ['web'],
  },
  web: {
    canImport: ['domain', 'application'],
    cannotImport: ['infrastructure'],
  },
};

// ============================================================================
// Finding ID Prefixes
// ============================================================================

export const FINDING_PREFIXES: Record<ReviewCategory, string> = {
  security: 'SEC',
  architecture: 'ARCH',
  'hardcoded-values': 'HARD',
  tests: 'TEST',
  'ai-hallucinations': 'AI',
  performance: 'PERF',
  'dead-code': 'DEAD',
  i18n: 'I18N',
  accessibility: 'A11Y',
};

// ============================================================================
// ID Generator
// ============================================================================

const findingCounters: Record<string, number> = {};

export function generateFindingId(category: ReviewCategory): string {
  const prefix = FINDING_PREFIXES[category];
  if (!findingCounters[prefix]) {
    findingCounters[prefix] = 0;
  }
  findingCounters[prefix]++;
  return `${prefix}-${String(findingCounters[prefix]).padStart(3, '0')}`;
}

export function resetFindingCounters(): void {
  Object.keys(findingCounters).forEach((key) => {
    findingCounters[key] = 0;
  });
}
